import {useRef, useState} from 'react'

import {useHubQuery} from '../../../../common/react-query/QueryProvider'
import {TransactionResultType} from '../types/confirmationResult'
import {mapRmcOrderRequestStatus} from '../utils/mapRmcOrderRequestStatus'

const AutomaticStatusCheckInterval = 5000
const MaximumAutomaticStatusChecks = 3

export interface RmcOrderStatus {
  requestId: string
  status: TransactionResultType
}

const shouldRetryStatus = (status: TransactionResultType) => status === 'pending'

export const useRmcOrderStatuses = (requestIds: string[]) => {
  const automaticStatusCheckCount = useRef(0)
  const [isAutomaticStatusCheckActive, setIsAutomaticStatusCheckActive] = useState(
    requestIds.length > 0
  )

  const queryInfo = useHubQuery('rmcOrderRequestStatuses', [requestIds], {
    enabled: requestIds.length > 0,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: isAutomaticStatusCheckActive ? AutomaticStatusCheckInterval : false,
    onSuccess: (responses) => {
      automaticStatusCheckCount.current += 1
      const shouldRetry = responses.some((response) =>
        shouldRetryStatus(mapRmcOrderRequestStatus(response))
      )

      if (!shouldRetry || automaticStatusCheckCount.current >= MaximumAutomaticStatusChecks) {
        setIsAutomaticStatusCheckActive(false)
      }
    },
    onError: () => setIsAutomaticStatusCheckActive(false)
  })

  const statuses: RmcOrderStatus[] =
    queryInfo.data?.map((response) => ({
      requestId: response.requestId,
      status: mapRmcOrderRequestStatus(response)
    })) ?? []

  return {
    statuses,
    isChecking: queryInfo.isFetching || isAutomaticStatusCheckActive,
    isError: queryInfo.isError,
    checkStatuses: queryInfo.refetch
  }
}
